import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAtom } from 'jotai'
import { newCardTokenAtom, cardInfoAtom, newCardInfoAtom, monthlyCourseIdAtom, restoreOrderIdAtom, payTypeAtom, payStartUrlAtom } from '@!/atoms/paymentAtom'
import { usePaymentSaveCard, useMonthlyCourse, useJumpLastUrl } from '@!/Hooks'
import { isBlank } from '@!/Util'
import RestoreConfirmView from '@!/User/Payment/components/MonthlyCourse/RestoreConfirm'

export const RestoreConfirm = () => {
  const navigate = useNavigate()
  const jumpLastUrl = useJumpLastUrl()
  const location = useLocation()

  const [monthlyCourseId] = useAtom(monthlyCourseIdAtom)
  const [restoreOrderId] = useAtom(restoreOrderIdAtom)
  const [newCardToken] = useAtom(newCardTokenAtom)
  const [newCardInfo] = useAtom(newCardInfoAtom)
  const [cardInfo] = useAtom(cardInfoAtom)
  const currentCard = newCardToken ? newCardInfo : cardInfo
  const [apiError, setApiError] = useState({ error: null, errorInfo: null })
  const [isComplete, setIsComplete] = useState(false)
  const [, setPayType] = useAtom(payTypeAtom)
  const [, setPayStartUrl] = useAtom(payStartUrlAtom)

  const [saveCardMutation, saveCardApiError] = usePaymentSaveCard()
  const { registerMonthlyCourseMutation, apiError: registerMonthlyCourseApiError } = useMonthlyCourse()

  useEffect(() => {
    if (!restoreOrderId) {
      jumpLastUrl()
      return
    }
    if (saveCardApiError?.error) setApiError(saveCardApiError)
    else if (registerMonthlyCourseApiError?.error) setApiError(registerMonthlyCourseApiError)
  }, [restoreOrderId, saveCardApiError, registerMonthlyCourseApiError])

  // カード保存成功時の処理
  useEffect(() => {
    if (saveCardMutation.isSuccess) {
      setApiError({ error: null, errorInfo: null })

      registerMonthlyCourseMutation.mutate({
        monthly_course_id: monthlyCourseId,
        restore_order_id: restoreOrderId,
        restore: true,
      })
    }
  }, [saveCardMutation.isSuccess])

  // 月額コース再登録成功時の処理
  useEffect(() => {
    if (registerMonthlyCourseMutation.isSuccess) {
      setApiError({ error: null, errorInfo: null })

      // 3Dセキュア認証画面へ遷移
      if (registerMonthlyCourseMutation.data.redirect_url) {
        setPayStartUrl(registerMonthlyCourseMutation.data.redirect_url)
        navigate('/redirect')
      } else {
        setIsComplete(true)
      }
    }
  }, [registerMonthlyCourseMutation.isSuccess])

  const handleSubmit = useCallback(() => {
    setPayType('credit')

    if (newCardToken) {
      // カード情報の更新処理
      const update = !isBlank(cardInfo)
      saveCardMutation.mutate({ token: newCardToken, isUpdate: update })
    } else {
      // カード情報の更新なしで月額コース再登録
      registerMonthlyCourseMutation.mutate({
        monthly_course_id: monthlyCourseId,
        restore_order_id: restoreOrderId,
        restore: true,
      })
    }
  }, [newCardToken, cardInfo, saveCardMutation, registerMonthlyCourseMutation, restoreOrderId])

  const handleChangeCard = useCallback(() => {
    navigate('/new_card')
  }, [])

  const handlePageBack = useCallback(() => {
    const from = location.state?.from
    if (from === 'RestoreRoot') {
      jumpLastUrl()
    } else {
      navigate(-1)
    }
  }, [])

  const handleComplete = useCallback(() => {
    jumpLastUrl()
  }, [])

  const handleApiErrorPopup = useCallback(() => {
    window.location.href = '/user/monthly_course_histories'
  }, [])

  const apiErrorText = useMemo(() => {
    if (!apiError.error) return ''
    if (apiError.error === 'fraudulent_registration') {
      return '登録回数が上限に達しました。時間をおいて再度お試しください。'
    }
    if (apiError.error.includes('card_info')) {
      const errorCode = apiError.errorInfo ? `(ErrorCode:${apiError.errorInfo})` : ''
      return `決済に失敗しました。入力内容をご確認ください${errorCode}`
    }
    if (apiError.error.includes('token_expire')) {
      return '決済に失敗しました。再度入力をお願いします。'
    }
    if (apiError.error === 'REGISTERED') {
      return 'すでに月額コースに登録済みです。'
    }
    if (apiError.error === 'CANCEL_RESERVED') {
      return '月額コースの解除処理中です。処理が完了するまで再登録できません。'
    }
    if (apiError.error === 'FAILED_RESTORE') {
      return 'この月額コースは再登録できません。新規登録を行ってください。'
    }
    if (apiError.error === 'FAILED_REGISTER') {
      return '月額コースの再登録に失敗しました。'
    }
    return 'エラーが発生しました。しばらく時間をおいて再度お試しください。'
  }, [apiError])

  return (
    <RestoreConfirmView
      newCardToken={newCardToken}
      currentCard={currentCard}
      handleSubmit={handleSubmit}
      handleChangeCard={handleChangeCard}
      handlePageBack={handlePageBack}
      handleApiErrorPopup={handleApiErrorPopup}
      handleComplete={handleComplete}
      apiErrorText={apiErrorText}
      isApiErrorOpen={!!apiError.error}
      isComplete={isComplete}
      isProcessing={saveCardMutation.isPending || registerMonthlyCourseMutation.isPending}
    />
  )
}

export default RestoreConfirm
